import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from '../config/api';
import { parseBlobError } from "../utils/parseBlobError";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { 
  Search, Filter, Users, UserCheck, Clock, CheckCircle2, AlertCircle, FileText,
  ChevronDown, ChevronUp, RefreshCw, Download, ExternalLink, X, Send, Loader2, Mail, Pencil, Trash2, Link, SkipForward, UserPlus
} from "lucide-react";
import { fmtUK, fmtUKShort } from "../lib/dateUtils";
import { toast } from "sonner";
import { InfoTooltip } from "./InfoTooltip";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useDirectorRole } from "../hooks/useDirectorRole";
import { obSendWithheldReason } from "../utils/obSendGuard";
import { clientDisplayName } from "../utils/clientDisplayName";

const STATUS_CONFIG = {
  // P3.4 fix: not_sent was bg-slate-800/50 text-slate-300 — dark text on dark bg.
  // Changed to a light slate surface with dark text so it's legible in both themes.
  not_sent: { label: "Not Sent", color: "bg-slate-200/80 text-slate-700 border-slate-400/60", icon: Clock },
  sent: { label: "Sent", color: "bg-blue-900/30 text-blue-400 border-blue-500/30", icon: Clock },
  partial: { label: "Partial", color: "bg-status-amber-dim/30 text-status-amber border-amber-500/30", icon: AlertCircle },
  complete: { label: "Complete", color: "bg-teal-dim/30 text-teal border-teal/30", icon: CheckCircle2 },
  overridden: { label: "Overridden", color: "bg-purple-900/30 text-purple-400 border-purple-500/30", icon: UserCheck },
  skipped: { label: "Skipped", color: "bg-gray-900/30 text-gray-400 border-gray-500/30", icon: SkipForward },
  // BM-H PR 1 (5 Sep 2026): both of these fell through to the "sent"
  // entry, so the row read "Sent" and said nothing true. waived_by_link is
  // a real terminal state the backend writes, and tob_save_failed means
  // the client DID come back and complete the form but the terms of
  // business PDF could not be filed. Reading "Sent" on either sends
  // whoever is looking at the row after the wrong thing.
  waived_by_link: { label: "Via Linked Matter", color: "bg-indigo-900/30 text-indigo-300 border-indigo-500/30", icon: UserCheck },
  tob_save_failed: { label: "Returned, filing failed", color: "bg-status-amber-dim/30 text-status-amber border-amber-500/30", icon: AlertCircle },
};

const DELIVERY_LABEL = {
  sent: { text: "Delivered", cls: "text-teal" },
  success: { text: "Delivered", cls: "text-teal" },
  error: { text: "Failed", cls: "text-status-red font-medium" },
  safe_mode: { text: "Safe Mode", cls: "text-status-amber" },
  not_configured: { text: "N/A", cls: "text-slate-400" },
};

// ── Poll until a predicate passes or we exhaust attempts ─────────────────────
async function pollUntil(fetchFn, predicate, { maxAttempts = 8, intervalMs = 800 } = {}) {
  for (let i = 0; i < maxAttempts; i++) {
    const data = await fetchFn();
    if (predicate(data)) return data;
    if (i < maxAttempts - 1) await new Promise(r => setTimeout(r, intervalMs));
  }
  return null;
}

export default function OnboardingStatusView({ 
  initialStatus = null, 
  initialFeeEarner = null,
  initialProvisionalIds = null,
  initialHighlight = null
}) {
  const getRef = (rec) => rec.matter_reference || rec.provisional_id;
  const displayRef = (ref) => {
    if (!ref) return "";
    if (ref.startsWith("OB-")) return `Legacy: ${ref}`;
    return ref;
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const [records, setRecords] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialStatus || searchParams.get("status") || "all");
  const [feeEarnerFilter, setFeeEarnerFilter] = useState(initialFeeEarner || searchParams.get("fee_earner") || "all");
  const [refIdFilter] = useState(initialProvisionalIds || searchParams.get("ids")?.split(",") || null);
  const [expandedId, setExpandedId] = useState(initialHighlight || null);
  const [resending, setResending] = useState(null);
  const [correctingId, setCorrectingId] = useState(null);
  const [correctionForm, setCorrectionForm] = useState({ client_name: "", client_email: "", correction_reason: "" });
  const [sendingOBFor, setSendingOBFor] = useState(null);
  const [obEmail, setObEmail] = useState("");
  const [sendingOB, setSendingOB] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(null);
  const [linkMatterRef, setLinkMatterRef] = useState("");
  const [linkNotes, setLinkNotes] = useState("");
  const [linking, setLinking] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(null);
  const [skipReason, setSkipReason] = useState("");
  const [skipMatterRef, setSkipMatterRef] = useState("");
  const [skipping, setSkipping] = useState(false);
  const [reminding, setReminding] = useState(null);
  // Add a joint client after the event (Vanessa, 30 Sep 2026).
  const [addingJointFor, setAddingJointFor] = useState(null);
  const [jointForm, setJointForm] = useState({ name: "", email: "", mobile: "" });
  const [addingJoint, setAddingJoint] = useState(false);
  const [resendingTerms, setResendingTerms] = useState(null);
  // Ambiguous-ref disambiguation modal state. Populated when a resend or
  // remind call returns HTTP 409 with a candidate list — the operator must
  // pick a specific matter_reference before the call is retried. The shape
  // mirrors backend candidate_summary: {matter_reference, provisional_id,
  // client_name, client_email, status, last_activity}.
  const [ambiguousResend, setAmbiguousResend] = useState(null);
  const [ambiguousRetryBusy, setAmbiguousRetryBusy] = useState(false);
  // Mutex fix (29 Apr 2026): cancel-and-restart replaces the "Skip OB
  // after sent" path. Lives alongside skip state so the modals don't
  // share buffers.
  const [showCancelModal, setShowCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  // Tracking panel data, keyed by ref. Populated lazily when the row
  // is expanded so the list view stays cheap.
  const [tracking, setTracking] = useState({});
  const [trackingLoading, setTrackingLoading] = useState({});

  const sendInFlight = useRef(new Set());
  const navigate = useNavigate();

  const { email: currentUserEmail, isAdmin } = useCurrentUser();
  // BM-H3324: AML Policy §7.5, only a director (the MLCO or the MLRO) may skip onboarding.
  const { isDirector } = useDirectorRole();

  useEffect(() => { fetchRecords(); }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
    if (feeEarnerFilter && feeEarnerFilter !== "all") params.set("fee_earner", feeEarnerFilter);
    setSearchParams(params, { replace: true });
  }, [statusFilter, feeEarnerFilter, setSearchParams]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await axios.get(`${API}/onboarding/list`);
      setRecords(response.data || []);
    } catch (err) {
      // Surface the failure inline alongside the toast so the stat
      // cards at the top of the view ("Total Clients", "Awaiting
      // Completion", "OB Complete", "Ready for FORF") are not
      // indistinguishable from a legitimate empty state when a 401 /
      // session-expired response comes back from /onboarding/list.
      const status = err?.response?.status;
      const reason = status === 401 || status === 403
        ? "Your session expired. Sign in again to load onboarding records."
        : errorMessage(err, "Failed to load onboarding records.");
      setLoadError(reason);
      setRecords([]);
      toast.error("Failed to load onboarding records");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSendFirstOB = async (rec) => {
    const guardKey = rec.provisional_id || rec.conflict_check_id;
    if (sendInFlight.current.has(guardKey)) return;
    if (!obEmail.trim() || !obEmail.includes("@")) {
      toast.error("Please enter a valid client email");
      return;
    }
    sendInFlight.current.add(guardKey);
    setSendingOB(true);
    try {
      const res = await axios.post(`${API}/onboarding/send-from-conflict-check`, {
        conflict_check_id: rec.conflict_check_id,
        client_name: rec.primary_client?.full_name || "Client",
        client_email: obEmail.trim(),
        client_type: rec.client_type || "Private individual",
        fee_earner: rec.fee_earner || "",
      });
      if (res.data.status === "already_sent") {
        toast.info(res.data.message);
      } else {
        toast.success(`OB invite sent to ${obEmail}`);
      }
      setSendingOBFor(null);
      setObEmail("");
      const targetCcId = rec.conflict_check_id;
      const appeared = await pollUntil(
        () => axios.get(`${API}/onboarding/list`).then(r => r.data || []),
        (rows) => rows.some(r => r.conflict_check_id === targetCcId && r.status !== "not_sent"),
        { maxAttempts: 8, intervalMs: 750 }
      );
      if (appeared) {
        setRecords(appeared);
      } else {
        await fetchRecords();
        toast.warning("Record may take a moment to appear — refresh if needed.");
      }
    } catch (e) {
      const fallback = "Failed to send onboarding link";
      const msg = errorMessage(e, fallback);
      toast.error(typeof msg === "string" ? msg : fallback);
    } finally {
      sendInFlight.current.delete(guardKey);
      setSendingOB(false);
    }
  };

  const clearFilters = () => {
    setStatusFilter("all");
    setFeeEarnerFilter("all");
    setSearchTerm("");
  };

  const hasActiveFilters = statusFilter !== "all" || feeEarnerFilter !== "all" || refIdFilter;

  const feeEarners = (() => {
    const set = new Set(records.map(r => r.fee_earner).filter(Boolean));
    ["Emily Howard", "Himanie Dubois", "Rebekah Brake Manning", "Sangita Gohil", "Vanessa Challess"].forEach(n => set.add(n));
    return [...set].sort();
  })();

  const filteredRecords = records.filter(rec => {
    if (refIdFilter?.length > 0 && !refIdFilter.includes(getRef(rec))) return false;
    if (feeEarnerFilter !== "all" && rec.fee_earner !== feeEarnerFilter) return false;
    if (statusFilter !== "all" && rec.status !== statusFilter) return false;
    const s = searchTerm.toLowerCase();
    if (!s) return true;
    return (
      clientDisplayName(rec).toLowerCase().includes(s) ||
      (rec.primary_client?.email || "").toLowerCase().includes(s) ||
      (getRef(rec) || "").toLowerCase().includes(s) ||
      (rec.fee_earner || "").toLowerCase().includes(s)
    );
  });

  const fmt = (d) => fmtUK(d);
  const fmtShort = (d) => fmtUKShort(d);

  const handleDownload = async (refId, docType) => {
    try {
      const response = await axios.get(`${API}/onboarding/documents/${refId}`, { params: { type: docType }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Bonsai_Law_${docType}_${refId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${docType === "audit_log" ? "Audit Log" : "OB Form"} downloaded`);
    } catch (err) {
      const detail = await parseBlobError(err);
      toast.error(`Failed to download document: ${detail}`);
    }
  };

  // Trust-context: backend returns HTTP 409 with a candidate list when a ref
  // resolves to >1 onboarding records. We must NOT silently retry against
  // the first hit — open the disambiguation modal and require the operator
  // to pick a specific matter_reference before retrying.
  const isAmbiguousResponse = (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status !== 409 || !detail || typeof detail !== "object") return false;
    const candidates = Array.isArray(detail.candidates) ? detail.candidates : [];
    return candidates.length > 0;
  };

  const openAmbiguousModal = (action, originalRef, detail) => {
    setAmbiguousResend({
      action,
      originalRef,
      candidates: detail.candidates || [],
      message: detail.message || "",
      ref: detail.ref || originalRef,
    });
  };

  // Vanessa, 30 Sep 2026: where nobody named the joint client at Send OB
  // and the main client did not name them on the form, staff had no way to
  // add and invite them afterwards. The backend sends the link now if the
  // main client has signed, or when they sign; the toast says which.
  const handleAddJoint = async (refId) => {
    setAddingJoint(true);
    try {
      const res = await axios.post(
        `${API}/onboarding/add-joint-client/${refId}`,
        jointForm,
        { headers: currentUserEmail ? { "X-Bonsai-User-Id": currentUserEmail } : {} },
      );
      const d = res.data || {};
      if (d.invitation_sent) {
        toast.success(`${jointForm.name} added and sent their onboarding link`);
      } else if (d.invitation_deferred) {
        toast.success(`${jointForm.name} added. Their link goes out when the main client signs.`);
      } else {
        toast.error(`${jointForm.name} added, but the email did not send: ${d.invitation_error || "unknown error"}. Use Resend OB Invite to try again.`);
      }
      setAddingJointFor(null);
      setJointForm({ name: "", email: "", mobile: "" });
      fetchRecords();
    } catch (e) {
      toast.error(errorMessage(e, "Could not add the joint client"));
    } finally {
      setAddingJoint(false);
    }
  };

  const handleResend = async (refId) => {
    setResending(refId);
    try {
      const res = await axios.post(`${API}/onboarding/resend-ob/${refId}`);
      if (res.data.email_error) {
        toast.error(`Delivery issue: ${res.data.email_error}`);
      } else {
        toast.success(`Onboarding invite resent for ${refId}`);
      }
      fetchRecords();
    } catch (e) {
      if (isAmbiguousResponse(e)) {
        openAmbiguousModal("resend", refId, e.response.data.detail);
      } else {
        toast.error(errorMessage(e, "Failed to resend"));
      }
    } finally {
      setResending(null);
    }
  };

  // BM-H3412 (1 Oct 2026): the client accepted, the filing failed. File the
  // snapshot they accepted instead of asking them to start again. Looks first
  // (dry run), says what it will do, and only files on a yes.
  const [retryingFiling, setRetryingFiling] = useState(null);
  const handleRetryFiling = async (refId) => {
    setRetryingFiling(refId);
    try {
      const look = await axios.post(`${API}/onboarding/retry-tob-filing/${refId}`, { dry_run: true });
      const d = look.data || {};
      if (!d.matter_folder_found) {
        toast.error(`The matter folder for ${refId} cannot be found on SharePoint${d.matter_folder_error ? ` (${d.matter_folder_error})` : ""}. Nothing was filed.`);
        return;
      }
      const ok = window.confirm(
        `File the Terms of Business the client accepted to ${d.destination}, as ${d.file_name}, and move this onboarding to "${d.would_set_status}"?`
      );
      if (!ok) return;
      const res = await axios.post(`${API}/onboarding/retry-tob-filing/${refId}`, {
        dry_run: false, reason: "Retried from the onboarding tracker",
      });
      toast.success(`Accepted Terms filed for ${refId}; onboarding now ${res.data.status}`);
      fetchRecords();
    } catch (e) {
      toast.error(errorMessage(e, "Filing failed again; the reason is on the record"));
      fetchRecords();
    } finally {
      setRetryingFiling(null);
    }
  };

  // BM-H547 (Vanessa, 16 September 2026): a client whose matter was linked or
  // skipped agrees terms from a signing link, not from the OB questionnaire.
  // Resend OB is the wrong button for them and Remind chases a form they were
  // never sent. This resends the Terms of Business signing link itself. The
  // token is deterministic, so the client's original email keeps working.
  const handleResendTerms = async (refId) => {
    setResendingTerms(refId);
    try {
      const res = await axios.post(`${API}/onboarding/resend-terms-of-business/${refId}`);
      toast.success(`Terms of Business link resent to ${res.data.sent_to || refId}`);
      fetchRecords();
    } catch (e) {
      if (isAmbiguousResponse(e)) {
        openAmbiguousModal("resend-terms", refId, e.response.data.detail);
      } else {
        toast.error(errorMessage(e, "Failed to resend the Terms of Business"));
      }
    } finally {
      setResendingTerms(null);
    }
  };

  const handleRemind = async (refId) => {
    setReminding(refId);
    try {
      const res = await axios.post(`${API}/onboarding/remind-ob/${refId}`);
      if (res.data.email_error) {
        toast.error(`Delivery issue: ${res.data.email_error}`);
      } else {
        toast.success(`Reminder sent for ${refId}`);
      }
      fetchRecords();
    } catch (e) {
      if (isAmbiguousResponse(e)) {
        openAmbiguousModal("remind", refId, e.response.data.detail);
      } else {
        toast.error(errorMessage(e, "Failed to send reminder"));
      }
    } finally {
      setReminding(null);
    }
  };

  const retryAmbiguous = async (chosenMatterRef) => {
    if (!ambiguousResend || !chosenMatterRef) return;
    const action = ambiguousResend.action;
    const path =
      action === "remind" ? "remind-ob"
      : action === "resend-terms" ? "resend-terms-of-business"
      : "resend-ob";
    setAmbiguousRetryBusy(true);
    try {
      const res = await axios.post(`${API}/onboarding/${path}/${chosenMatterRef}`);
      if (res.data.email_error) {
        toast.error(`Delivery issue: ${res.data.email_error}`);
      } else {
        toast.success(
          action === "remind"
            ? `Reminder sent for ${chosenMatterRef}`
            : action === "resend-terms"
              ? `Terms of Business link resent for ${chosenMatterRef}`
              : `Onboarding invite resent for ${chosenMatterRef}`
        );
      }
      setAmbiguousResend(null);
      fetchRecords();
    } catch (e) {
      // If the chosen ref is itself ambiguous (vanishingly unlikely but
      // possible if two records share an exact matter_reference), update
      // the modal rather than silently failing.
      if (isAmbiguousResponse(e)) {
        openAmbiguousModal(action, chosenMatterRef, e.response.data.detail);
      } else {
        toast.error(errorMessage(e, "Retry failed"));
      }
    } finally {
      setAmbiguousRetryBusy(false);
    }
  };

  const deliveryLabel = (d, rec) => {
    const cfg = DELIVERY_LABEL[d];
    if (cfg) return <span className={cfg.cls}>{cfg.text}</span>;
    if (rec?.email_sent_at) {
      return <span className="text-blue-400 text-[10px]" title="Email was sent but delivery tracking was not available">Sent</span>;
    }
    if (!d || d === "unknown" || d === "none") {
      return <span className="text-slate-400 italic text-[10px]" title="Record created before email tracking was implemented">Historical</span>;
    }
    return <span className="text-slate-400">-</span>;
  };

  const handleCorrection = async (refId) => {
    if (!correctionForm.client_name.trim() && !correctionForm.client_email.trim()) {
      return toast.error("Enter at least a corrected name or email");
    }
    if (!correctionForm.correction_reason.trim()) {
      return toast.error("Correction reason is required for audit trail");
    }
    try {
      const res = await axios.post(`${API}/onboarding/correct-client/${refId}`, {
        ...correctionForm,
        corrected_by: sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || sessionStorage.getItem("bonsai_user_id") || "director",
      });
      if (res.data.success) {
        toast.success(res.data.message);
        setCorrectingId(null);
        setCorrectionForm({ client_name: "", client_email: "", correction_reason: "" });
        fetchRecords();
      }
    } catch (err) {
      toast.error(errorMessage(err, "Correction failed"));
    }
  };

  const toggleSelect = (pid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredRecords.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredRecords.map(r => getRef(r))));
    }
  };

  const handleBulkDelete = async () => {
    if (!deleteReason.trim()) return toast.error("Deletion reason is required");
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const res = await axios.delete(`${API}/onboarding/records/bulk`, {
        data: { provisional_ids: [...selectedIds], matter_references: [...selectedIds], reason: deleteReason.trim() }
      });
      toast.success(res.data.message || `Deleted ${selectedIds.size} records`);
      setSelectedIds(new Set());
      setShowDeleteConfirm(false);
      setDeleteReason("");
      fetchRecords();
    } catch (err) {
      toast.error(errorMessage(err, "Bulk delete failed"));
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleLinkOB = async () => {
    if (!linkMatterRef.trim()) return toast.error("Enter a matter reference");
    setLinking(true);
    try {
      await axios.post(`${API}/onboarding/link/${showLinkModal}`, { target_matter_reference: linkMatterRef.trim(), notes: linkNotes.trim(), method: linkNotes.trim() || "Manually completed" }, { headers: { "x-user-email": currentUserEmail } });
      toast.success(`OB linked to ${linkMatterRef.trim()}`);
      setShowLinkModal(null);
      setLinkMatterRef("");
      fetchRecords();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to link OB"));
    } finally {
      setLinking(false);
    }
  };

  const handleSkipOB = async () => {
    setSkipping(true);
    try {
      if (!skipReason.trim()) return toast.error("Reason is required to skip OB");
      if (!skipMatterRef.trim()) return toast.error("Matter reference is required to skip OB");
      await axios.post(`${API}/onboarding/skip/${showSkipModal}`, { reason: skipReason.trim(), linked_matter_reference: skipMatterRef.trim() }, { headers: { "x-user-email": currentUserEmail } });
      toast.success("OB marked as skipped");
      setShowSkipModal(null);
      setSkipReason("");
      fetchRecords();
    } catch (err) {
      // 409 = state machine refused (e.g. Skip after Sent). Surface
      // the backend reason verbatim — it's the authoritative answer.
      const status = err.response?.status;
      const reason = errorMessage(err, status === 409 ? "Skip not permitted in current state" : "Failed to skip OB");
      toast.error(reason);
    } finally {
      setSkipping(false);
    }
  };

  const handleCancelAndRestart = async () => {
    setCancelling(true);
    try {
      if (!cancelReason.trim()) return toast.error("Reason is required to cancel an in-flight OB");
      await axios.post(
        `${API}/onboarding/cancel-and-restart/${showCancelModal}`,
        { reason: cancelReason.trim() },
        { headers: { "x-user-email": currentUserEmail } }
      );
      toast.success("OB cancelled — record reverted to Not Sent");
      setShowCancelModal(null);
      setCancelReason("");
      fetchRecords();
    } catch (err) {
      const status = err.response?.status;
      const reason = errorMessage(err, status === 409 ? "Cancel not permitted in current state" : "Failed to cancel OB");
      toast.error(reason);
    } finally {
      setCancelling(false);
    }
  };

  const fetchTracking = useCallback(async (ref) => {
    if (!ref) return;
    setTrackingLoading(prev => ({ ...prev, [ref]: true }));
    try {
      const resp = await axios.get(`${API}/onboarding/tracking/${ref}`, {
        headers: { "x-user-email": currentUserEmail },
      });
      setTracking(prev => ({ ...prev, [ref]: resp.data }));
    } catch (err) {
      // Non-fatal: the tracking panel degrades gracefully when the
      // endpoint is unreachable; the inline email_delivery row still
      // renders from the list payload.
      // eslint-disable-next-line no-console
      console.warn("tracking fetch failed", ref, err?.response?.status);
    } finally {
      setTrackingLoading(prev => ({ ...prev, [ref]: false }));
    }
  }, [currentUserEmail]);

  const stats = {
    total: records.length,
    inProgress: records.filter(r => ["not_sent", "sent", "partial"].includes(r.status)).length,
    complete: records.filter(r => r.status === "complete" || r.status === "overridden").length,
    forfReady: records.filter(r => r.draft_forf_id).length,
    emailFail: records.filter(r => r.email_delivery === "error" || r.email_delivery === "smtp_error").length,
  };

  const isExpanded = (id) => expandedId === id;

  return (
    <div className="space-y-5" data-testid="onboarding-status-view">
      {loadError && (
        <div
          className="flex items-start gap-2 rounded-md border border-red-500/40 bg-status-red-dim/20 px-3 py-2 text-sm text-status-red"
          data-testid="onboarding-load-error"
          role="alert"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Could not load onboarding records.</p>
            <p className="text-xs text-status-red/80">{loadError}</p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-navy-hover/60 border border-navy-border rounded-md px-3 py-3 text-center">
          <p className="text-2xl font-bold text-white">{stats.total}</p>
          <p className="text-[11px] text-txt-muted">Total Clients</p>
        </div>
        <div className="bg-navy-hover/60 border border-amber-500/40 rounded-md px-3 py-3 text-center">
          <p className="text-2xl font-bold text-status-amber">{stats.inProgress}</p>
          <p className="text-[11px] text-status-amber/70">Awaiting Completion</p>
        </div>
        <div className="bg-navy-hover/60 border border-teal/40 rounded-md px-3 py-3 text-center">
          <p className="text-2xl font-bold text-teal">{stats.complete}</p>
          <p className="text-[11px] text-teal/70">OB Complete</p>
        </div>
        <div className="bg-navy-hover/60 border border-teal/50 rounded-md px-3 py-3 text-center">
          <p className="text-2xl font-bold text-teal">{stats.forfReady}</p>
          <p className="text-[11px] text-teal/70">Ready for FORF</p>
        </div>
      </div>

      {stats.emailFail > 0 && (
        <div className="flex items-center gap-2 bg-status-red-dim/20 border border-red-500/30 rounded-md px-3 py-2 text-sm text-status-red">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span className="flex-1">{stats.emailFail} email{stats.emailFail > 1 ? "s" : ""} failed to deliver — use <strong>Resend OB Invite</strong> to retry</span>
          <button
            type="button"
            onClick={() => navigate("/ob-email-log")}
            data-testid="onboarding-open-email-log-alert"
            className="shrink-0 inline-flex items-center gap-1 text-xs font-medium underline hover:no-underline"
          >
            <Mail className="w-3 h-3" /> View delivery log
          </button>
        </div>
      )}

      {/* Full onboarding email delivery log (searchable, all statuses). Lives at
          /ob-email-log; this is its entry point from the onboarding surface. */}
      <div className="flex justify-end -mt-1">
        <button
          type="button"
          onClick={() => navigate("/ob-email-log")}
          data-testid="onboarding-open-email-log"
          className="inline-flex items-center gap-1.5 text-xs text-txt-muted hover:text-teal transition-colors"
        >
          <Mail className="w-3.5 h-3.5" /> Onboarding email delivery log
          <ExternalLink className="w-3 h-3 opacity-70" />
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between">
        <div className="flex flex-wrap gap-2 flex-1">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-9 text-xs border-navy-border bg-navy-hover text-txt-muted" data-testid="onboarding-status-filter">
              <Filter className="w-3 h-3 mr-1" /><SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="not_sent">Not Sent</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
              <SelectItem value="overridden">Overridden</SelectItem>
              <SelectItem value="skipped">Skipped</SelectItem>
            </SelectContent>
          </Select>
          <Select value={feeEarnerFilter} onValueChange={setFeeEarnerFilter}>
            <SelectTrigger className="w-44 h-9 text-xs border-navy-border bg-navy-hover text-txt-muted" data-testid="onboarding-fee-earner-filter">
              <Users className="w-3 h-3 mr-1" /><SelectValue placeholder="All Fee Earners" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Fee Earners</SelectItem>
              {feeEarners.map(fe => <SelectItem key={fe} value={fe}>{fe}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative w-56">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-txt-muted" />
            <Input
              placeholder="Search name, email, ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-9 text-xs border-navy-border bg-navy-hover text-white placeholder-[#7a86a8]"
              data-testid="onboarding-search-input"
            />
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-xs text-txt-muted hover:text-white" data-testid="clear-filters-btn">
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={fetchRecords} className="h-9 gap-1.5 text-xs border-navy-border text-txt-muted hover:bg-navy-hover hover:text-white" data-testid="onboarding-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </Button>
        {selectedIds.size > 0 && (
          <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(true)}
            className="h-9 gap-1.5 text-xs border-red-500/40 text-status-red hover:bg-status-red-dim/20 hover:text-status-red"
            data-testid="bulk-delete-btn">
            <Trash2 className="w-3.5 h-3.5" /> Delete {selectedIds.size} selected
          </Button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="p-12 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-teal" />
          <p className="mt-3 text-sm text-txt-muted">Loading...</p>
        </div>
      ) : filteredRecords.length === 0 ? (
        <div className="p-12 text-center bg-navy-hover/40 rounded-md border border-dashed border-navy-border">
          <Users className="w-10 h-10 text-txt-faint mx-auto mb-3" />
          <p className="text-txt-muted text-sm">No onboarding records found</p>
        </div>
      ) : (
        <div className="border border-navy-border rounded-md overflow-hidden" data-testid="onboarding-table">
          {/* Header */}
          <div className="hidden lg:grid lg:grid-cols-[32px_2fr_90px_90px_90px_180px_80px] gap-2 px-4 py-2.5 bg-teal/20 border-b border-teal/30 text-[11px] font-bold uppercase tracking-wider text-teal">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={filteredRecords.length > 0 && selectedIds.size === filteredRecords.length}
                onChange={toggleSelectAll}
                className="w-3.5 h-3.5 rounded border-navy-border accent-teal cursor-pointer"
                data-testid="select-all-checkbox"
              />
            </div>
            <div>Client</div>
            <div>Date</div>
            <div>OB Status</div>
            <div>Delivery</div>
            <div>Actions</div>
            <div className="text-right">Audit Log</div>
          </div>

          {/* Rows */}
          <div className="divide-y divide-navy-border/50">
            {filteredRecords.map((rec) => {
              const ref = getRef(rec);
              const expanded = isExpanded(ref);
              const hasFORF = !!rec.draft_forf_id;
              const cfg = STATUS_CONFIG[rec.status] || STATUS_CONFIG.sent;
              // BM-H278 (Vanessa, 4 Sep 2026): Resend OB and Remind were
              // offered for every status that was neither complete nor
              // not_sent, which caught skipped, overridden, waived_by_link
              // and returned-offline rows. A satisfied OB gets its state
              // stated instead of a pair of send buttons.
              const obWithheld = obSendWithheldReason(rec);
              const Icon = cfg.icon;
              const jcs = rec.joint_clients || [];
              const jcComplete = jcs.filter(j => j.status === "completed").length;
              // BM-H PR 1 (5 Sep 2026): this counter used to show joint
              // parties only, so a matter where the joint client had
              // submitted and the MAIN client had not read "1/1", which
              // looks finished and is not. Everyone who has to sign is
              // counted, main client included, and the tooltip names who
              // is still outstanding.
              const primaryIn = rec.primary_client?.agreed_tob === true
                && !!(rec.primary_client?.tob_accepted_at || rec.primary_client?.accepted_at);
              const partiesTotal = jcs.length + 1;
              const partiesIn = jcComplete + (primaryIn ? 1 : 0);
              const outstanding = [
                ...(primaryIn ? [] : [rec.primary_client?.full_name || "Main client"]),
                ...jcs.filter(j => j.status !== "completed").map(j => j.name || j.email || "Joint client"),
              ];
              const partiesTitle = outstanding.length
                ? `${partiesIn} of ${partiesTotal} signed. Outstanding: ${outstanding.join(", ")}`
                : `All ${partiesTotal} parties have signed`;

              return (
                <div key={ref} className={`${expanded ? "bg-navy-hover/80" : "hover:bg-navy-hover/40"} transition-colors`} data-testid={`onboarding-record-${ref}`}>
                  <div
                    role="button" tabIndex={0}
                    className="w-full text-left px-4 py-3 lg:grid lg:grid-cols-[32px_2fr_90px_90px_90px_180px_80px] lg:gap-2 lg:items-center flex flex-wrap items-center gap-2 cursor-pointer"
                    onClick={() => {
                      const next = expanded ? null : ref;
                      setExpandedId(next);
                      if (next && !tracking[ref]) fetchTracking(ref);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const next = expanded ? null : ref;
                        setExpandedId(next);
                        if (next && !tracking[ref]) fetchTracking(ref);
                      }
                    }}
                  >
                    <div className="flex items-center" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.has(ref)} onChange={() => toggleSelect(ref)}
                        className="w-3.5 h-3.5 rounded border-navy-border accent-teal cursor-pointer"
                        data-testid={`select-${ref}`} />
                    </div>
                    <div className="min-w-0 flex-1 lg:flex-none">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-sm text-white truncate">{clientDisplayName(rec) || "Unknown"}</span>
                        <span className="text-[10px] text-txt-muted font-mono shrink-0">{displayRef(ref)}</span>
                        {jcs.length > 0 && (
                          <span className="text-[10px] text-txt-muted shrink-0" title={partiesTitle}
                            data-testid={`party-count-${ref}`}>
                            <Users className="w-3 h-3 inline" /> {partiesIn}/{partiesTotal}
                          </span>
                        )}
                        {hasFORF && <FileText className="w-3 h-3 text-teal shrink-0" title="FORF Ready" />}
                      </div>
                    </div>
                    <div className="text-[11px] text-txt-muted hidden lg:block">{fmtShort(rec.created_at)}</div>
                    <div>
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded border ${cfg.color}`}>
                        <Icon className="w-3 h-3" /> {cfg.label}
                      </span>
                    </div>
                    <div className="text-xs">
                      {rec.status === "not_sent" ? <span className="text-slate-400">-</span> : deliveryLabel(rec.email_delivery, rec)}
                    </div>
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      {obWithheld && (
                        <span className="text-[11px] text-teal" data-testid={`ob-satisfied-${ref}`}>
                          {obWithheld}
                        </span>
                      )}
                      {!obWithheld && rec.status === "not_sent" && (
                        <Button variant="ghost" size="sm"
                          className="h-7 px-2 text-[11px] text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                          onClick={() => { setSendingOBFor(ref); setExpandedId(ref); setObEmail(""); }}
                          data-testid={`send-ob-${ref}`} title="Send onboarding invite to client">
                          <Send className="w-3 h-3" /><span className="ml-1">Send OB</span>
                        </Button>
                      )}
                      {!obWithheld && rec.status !== "complete" && rec.status !== "not_sent" && (
                        <>
                          <Button variant="ghost" size="sm"
                            className="h-7 px-2 text-[11px] text-teal hover:text-teal hover:bg-teal-dim/20"
                            onClick={() => {
                              const recEmail = rec.primary_client?.email || rec.client_email || "";
                              if (!recEmail) { setSendingOBFor(ref); setExpandedId(ref); setObEmail(""); }
                              else { handleResend(ref); }
                            }}
                            disabled={resending === ref}
                            data-testid={`resend-full-${ref}`} title="Resend onboarding invitation link">
                            {resending === ref ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                            <span className="ml-1">Resend OB</span>
                          </Button>
                          <Button variant="ghost" size="sm"
                            className="h-7 px-2 text-[11px] text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                            onClick={() => handleRemind(ref)}
                            disabled={reminding === ref}
                            data-testid={`remind-${ref}`} title="Send reminder with onboarding link">
                            {reminding === ref ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                            <span className="ml-1">Remind</span>
                          </Button>
                        </>
                      )}
                      {/* BM-H547: shown only where a terms request exists and
                          the client has not yet signed. A signed retainer is
                          not re-signable, so the button goes away on
                          acceptance rather than offering a second signature
                          over a recorded one. */}
                      {!!rec.terms_request_reason && !primaryIn && (
                        <Button variant="ghost" size="sm"
                          className="h-7 px-2 text-[11px] text-status-amber hover:text-status-amber hover:bg-status-amber-dim/20"
                          onClick={() => handleResendTerms(ref)}
                          disabled={resendingTerms === ref}
                          data-testid={`resend-terms-${ref}`}
                          title="Resend the Terms of Business signing link to the client">
                          {resendingTerms === ref ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                          <span className="ml-1">Resend ToB</span>
                        </Button>
                      )}
                    </div>
                    <div className="flex items-center gap-1 justify-end" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="sm"
                        className="h-7 px-2 text-[11px] text-status-amber/70 hover:text-status-amber hover:bg-status-amber-dim/20"
                        onClick={() => {
                          setCorrectingId(correctingId === ref ? null : ref);
                          setCorrectionForm({ client_name: rec.primary_client?.full_name || "", client_email: rec.primary_client?.email || "", correction_reason: "" });
                        }}
                        data-testid={`correct-client-${ref}`} title="Correct client name/details">
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm"
                        className="h-7 px-2 text-[11px] text-txt-muted hover:text-white hover:bg-navy-hover"
                        onClick={() => handleDownload(ref, "audit_log")}
                        data-testid={`download-audit-${ref}`} title="Download Audit Log">
                        <Download className="w-3 h-3" />
                      </Button>
                      {expanded ? <ChevronUp className="w-4 h-4 text-txt-muted" /> : <ChevronDown className="w-4 h-4 text-txt-muted" />}
                    </div>
                  </div>

                  {expanded && (
                    <div className="border-t border-navy-border bg-navy/80 px-4 py-4 space-y-4">
                      {correctingId === ref && (
                        <div className="bg-status-amber-dim/20 border border-amber-700/40 rounded-lg p-3 space-y-2" data-testid="correction-form">
                          <p className="text-xs font-semibold text-status-amber flex items-center gap-1"><Pencil className="w-3 h-3" /> Correct Client Details</p>
                          <p className="text-[10px] text-status-amber/70">Changes are audit-logged. Leave fields blank to keep current values.</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <div>
                              <label className="text-[10px] text-status-amber/60 block mb-0.5">Client Name</label>
                              <Input value={correctionForm.client_name} onChange={e => setCorrectionForm(f => ({...f, client_name: e.target.value}))}
                                className="bg-navy-card border-amber-700/40 text-white text-xs h-8" data-testid="correction-name" />
                            </div>
                            <div>
                              <label className="text-[10px] text-status-amber/60 block mb-0.5">Client Email</label>
                              <Input value={correctionForm.client_email} onChange={e => setCorrectionForm(f => ({...f, client_email: e.target.value}))}
                                className="bg-navy-card border-amber-700/40 text-white text-xs h-8" data-testid="correction-email" />
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] text-status-amber/60 block mb-0.5">Reason for Correction *</label>
                            <Input value={correctionForm.correction_reason} onChange={e => setCorrectionForm(f => ({...f, correction_reason: e.target.value}))}
                              placeholder="e.g. Wrong name entered — Leo Kusi-Appiah is other side, not client"
                              className="bg-navy-card border-amber-700/40 text-white text-xs h-8" data-testid="correction-reason" />
                          </div>
                          <div className="flex gap-2 pt-1">
                            <Button size="sm" onClick={() => handleCorrection(ref)} className="bg-status-amber hover:bg-status-amber text-white text-xs h-7" data-testid="save-correction-btn">
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Save Correction
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setCorrectingId(null)} className="text-gray-400 text-xs h-7">Cancel</Button>
                          </div>
                        </div>
                      )}

                      {rec.status === "not_sent" && sendingOBFor === ref && (
                        <div className="bg-blue-950/30 border border-blue-500/30 rounded-lg p-3 space-y-2" data-testid="send-ob-form">
                          <p className="text-xs font-semibold text-blue-300 flex items-center gap-1"><Send className="w-3 h-3" /> Send Client Onboarding Invite</p>
                          <p className="text-[10px] text-blue-400/70">Enter the client&apos;s email to send the onboarding invite link for {rec.primary_client?.full_name}.</p>
                          <div className="flex gap-2 items-end">
                            <div className="flex-1">
                              <label className="text-[10px] text-blue-200/60 block mb-0.5">Client Email *</label>
                              <Input type="email" value={obEmail} onChange={e => setObEmail(e.target.value)}
                                placeholder="client@example.com"
                                className="bg-navy-card border-blue-700/40 text-white text-xs h-8"
                                data-testid="ob-client-email" autoFocus
                                onKeyDown={e => { if (e.key === "Enter" && !sendingOB) handleSendFirstOB(rec); }}
                              />
                            </div>
                            <Button size="sm" onClick={() => handleSendFirstOB(rec)}
                              disabled={sendingOB || sendInFlight.current.has(rec.provisional_id || rec.conflict_check_id)}
                              className="bg-blue-600 hover:bg-blue-500 text-white text-xs h-8" data-testid="ob-send-btn">
                              {sendingOB ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                              Send
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setSendingOBFor(null)} className="text-gray-400 text-xs h-8">Cancel</Button>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                        <Detail label="Name" value={rec.primary_client?.full_name} />
                        <Detail label="Email" value={rec.primary_client?.email} />
                        <Detail label="Phone" value={rec.primary_client?.phone} />
                        <Detail label="Entity" value={rec.entity_type} capitalize />
                        <Detail label="Fee Earner" value={rec.fee_earner} />
                        <Detail label="TOB Accepted" value={rec.primary_client?.agreed_tob ? "Yes" : "No"} color={rec.primary_client?.agreed_tob ? "text-teal" : "text-status-red"} />
                        <Detail label="TOB Accepted At" value={fmt(rec.primary_client?.accepted_at)} />
                        <Detail label="Created" value={fmt(rec.created_at)} />
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm border-t border-navy-border pt-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-txt-muted mb-0.5">Email Delivery</p>
                          {(() => {
                            const d = rec.email_delivery;
                            if (d === "sent" || d === "success") return <span className="text-teal font-medium text-sm">Successful</span>;
                            if (d === "error") return <span className="text-status-red font-medium text-sm">Failed</span>;
                            if (d === "safe_mode") return <span className="text-status-amber text-sm">Safe Mode</span>;
                            if (!rec.email_sent_at) return <span className="text-txt-muted italic text-sm">Historical record — no tracking data</span>;
                            return <span className="text-txt-muted text-sm">Sent (untracked)</span>;
                          })()}
                        </div>
                        <Detail label="Sent At" value={fmt(rec.email_sent_at)} />
                        {rec.last_resent_at && <Detail label="Last Resent" value={fmt(rec.last_resent_at)} />}
                        {rec.email_error && (
                          <div className="col-span-2">
                            <p className="text-[10px] uppercase tracking-wider text-txt-muted mb-0.5">Error</p>
                            <p className="text-status-red text-xs">{rec.email_error}</p>
                          </div>
                        )}
                      </div>

                      {/* Tracking panel — visible whenever the OB email
                          has gone out. Pulls from /onboarding/tracking
                          which joins the OB record with the
                          notifications_log audit rows so the fee earner
                          can see exactly which email was sent, to whom,
                          when, and what the dispatch outcome was. */}
                      {(rec.status === "sent" || rec.status === "partial" ||
                        rec.status === "complete" || rec.status === "tob_save_failed" ||
                        rec.status === "overridden") && (
                        <div className="border-t border-navy-border pt-3" data-testid={`tracking-panel-${ref}`}>
                          <p className="text-[10px] uppercase tracking-wider text-txt-muted mb-2 flex items-center gap-2">
                            <Mail className="w-3 h-3" /> OB Email Tracking
                            {trackingLoading[ref] && <Loader2 className="w-3 h-3 animate-spin" />}
                          </p>
                          {(() => {
                            const t = tracking[ref];
                            if (!t && !trackingLoading[ref]) {
                              return <p className="text-xs text-txt-muted italic">Tracking data unavailable. <button className="underline" onClick={() => fetchTracking(ref)} data-testid={`tracking-retry-${ref}`}>Retry</button></p>;
                            }
                            if (!t) return <p className="text-xs text-txt-muted italic">Loading…</p>;
                            const events = t.events || [];
                            return (
                              <div className="space-y-2">
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs bg-navy-card/40 border border-navy-border rounded-md p-2">
                                  <div>
                                    <p className="text-[10px] text-txt-muted">Recipient</p>
                                    <p className="text-white truncate">{t.primary?.recipient_email || "-"}</p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] text-txt-muted">Sent At</p>
                                    <p className="text-white">{fmt(t.primary?.sent_at)}</p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] text-txt-muted">Delivery</p>
                                    <p className={
                                      (t.primary?.delivery === "sent" || t.primary?.delivery === "success") ? "text-teal" :
                                      t.primary?.delivery === "error" ? "text-status-red" :
                                      "text-txt-muted"
                                    }>{t.primary?.delivery || "unknown"}</p>
                                  </div>
                                  {t.primary?.message_id && (
                                    <div className="col-span-2 md:col-span-3">
                                      <p className="text-[10px] text-txt-muted">Message-ID</p>
                                      <p className="text-white font-mono text-[11px] truncate">{t.primary.message_id}</p>
                                    </div>
                                  )}
                                  {t.primary?.delivery_error && (
                                    <div className="col-span-2 md:col-span-3">
                                      <p className="text-[10px] text-status-red/70">Delivery Error</p>
                                      <p className="text-status-red text-xs">{t.primary.delivery_error}</p>
                                    </div>
                                  )}
                                  {t.primary?.tob_accepted && (
                                    <div>
                                      <p className="text-[10px] text-txt-muted">TOB Accepted</p>
                                      <p className="text-teal">{fmt(t.primary?.submitted_at) || "Yes"}</p>
                                    </div>
                                  )}
                                </div>
                                {events.length > 0 && (
                                  <details className="text-xs">
                                    <summary className="cursor-pointer text-txt-muted hover:text-white">
                                      Notification audit ({events.length} event{events.length !== 1 ? "s" : ""})
                                    </summary>
                                    <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                                      {events.map((ev, i) => (
                                        <div key={i} className="bg-navy-card/40 border border-navy-border rounded p-2" data-testid={`tracking-event-${i}`}>
                                          <div className="flex justify-between items-center text-[11px]">
                                            <span className="font-mono text-blue-300">{ev.event_type}</span>
                                            <span className="text-txt-muted">{fmt(ev.timestamp)}</span>
                                          </div>
                                          <p className="text-[11px] text-txt-muted truncate">to: {(ev.recipients || []).join(", ") || "-"}</p>
                                          <p className={`text-[11px] ${ev.status === "sent" || ev.status === "success" ? "text-teal" : ev.status === "error" ? "text-status-red" : "text-txt-muted"}`}>
                                            {ev.status}{ev.error ? ` — ${ev.error}` : ""}
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {rec.status !== "not_sent" && (
                        <div className="border-t border-navy-border pt-3" data-testid={`add-joint-section-${ref}`}>
                          {addingJointFor === ref ? (
                            <div className="bg-blue-950/30 border border-blue-500/30 rounded-lg p-3 space-y-2" data-testid="add-joint-form">
                              <p className="text-xs text-txt-muted">
                                The joint client is sent their own onboarding link now if the main client has signed, or as soon as they sign.
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <Input placeholder="Full name" value={jointForm.name}
                                  onChange={e => setJointForm(f => ({ ...f, name: e.target.value }))}
                                  className="bg-navy-card border-blue-500/30 text-white text-xs h-8" data-testid="add-joint-name" autoFocus />
                                <Input placeholder="Email" type="email" value={jointForm.email}
                                  onChange={e => setJointForm(f => ({ ...f, email: e.target.value }))}
                                  className="bg-navy-card border-blue-500/30 text-white text-xs h-8" data-testid="add-joint-email" />
                                <Input placeholder="Mobile (for Thirdfort)" value={jointForm.mobile}
                                  onChange={e => setJointForm(f => ({ ...f, mobile: e.target.value }))}
                                  className="bg-navy-card border-blue-500/30 text-white text-xs h-8" data-testid="add-joint-mobile" />
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => handleAddJoint(ref)}
                                  disabled={addingJoint || !jointForm.name.trim() || !jointForm.email.trim()}
                                  className="bg-blue-600 hover:bg-blue-500 text-white text-xs h-8" data-testid="add-joint-submit">
                                  {addingJoint ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />} Add and send link
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setAddingJointFor(null)}
                                  className="text-xs h-8 text-txt-muted" data-testid="add-joint-cancel">Cancel</Button>
                              </div>
                            </div>
                          ) : (
                            <Button variant="outline" size="sm"
                              className="gap-1 text-xs border-blue-500/30 text-blue-400 hover:bg-blue-900/20"
                              onClick={() => { setAddingJointFor(ref); setJointForm({ name: "", email: "", mobile: "" }); }}
                              data-testid={`add-joint-${ref}`}>
                              <UserPlus className="w-3 h-3" /> Add joint client
                            </Button>
                          )}
                        </div>
                      )}

                      {jcs.length > 0 && (
                        <div className="border-t border-navy-border pt-3">
                          <p className="text-[10px] uppercase tracking-wider text-txt-muted mb-2">Joint Clients ({jcs.length})</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {jcs.map((jc, idx) => (
                              <div key={idx} className={`flex items-center justify-between p-2.5 rounded-md text-sm ${jc.status === "completed" ? "bg-teal-dim/20 border border-teal/30" : "bg-status-amber-dim/20 border border-amber-500/30"}`}>
                                <div className="min-w-0">
                                  <p className="font-medium text-white truncate">{jc.name}</p>
                                  <p className="text-xs text-txt-muted truncate">{jc.email}</p>
                                  {/* Brief 37: surface mobile at the admin Thirdfort dispatch point.
                                      Always render the row (even if blank) so admin can spot missing
                                      numbers at a glance and chase before dispatching. */}
                                  <p className={`text-xs truncate ${jc.mobile ? "text-txt-muted" : "text-status-amber"}`} data-testid={`jc-mobile-${idx}`}>
                                    {jc.mobile || "Mobile: missing — chase before Thirdfort"}
                                  </p>
                                </div>
                                <Badge variant="outline" className={jc.status === "completed" ? "bg-teal-dim/30 text-teal border-teal/30 text-[10px]" : "bg-status-amber-dim/30 text-status-amber border-amber-500/30 text-[10px]"}>
                                  {jc.status === "completed" ? <><CheckCircle2 className="w-3 h-3 mr-0.5" /> Done</> : <><Clock className="w-3 h-3 mr-0.5" /> Pending</>}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {rec.status === "overridden" && (
                        <div className="bg-purple-900/20 border border-purple-500/30 rounded-md p-3 text-sm">
                          <p className="text-[10px] uppercase tracking-wider text-purple-400 mb-1 font-semibold">Override Applied</p>
                          <p className="text-txt-muted"><strong>Reason:</strong> {rec.override_reason || "-"}</p>
                          <p className="text-xs text-txt-muted mt-1">At: {fmt(rec.overridden_at)}</p>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 border-t border-navy-border pt-3">
                        <Button variant="outline" size="sm" className="gap-1 text-xs border-navy-border text-txt-muted hover:bg-navy-hover hover:text-white" onClick={() => handleDownload(ref, "audit_log")} data-testid={`download-audit-exp-${ref}`}>
                          <Download className="w-3 h-3" /> Audit Log
                        </Button>
                        <Button variant="outline" size="sm" className="gap-1 text-xs border-navy-border text-txt-muted hover:bg-navy-hover hover:text-white" onClick={() => handleDownload(ref, "ob_form")} data-testid={`download-ob-${ref}`}>
                          <Download className="w-3 h-3" /> OB Form
                        </Button>
                        {!obWithheld && rec.status !== "complete" && rec.status !== "not_sent" && (
                          <>
                            <Button variant="outline" size="sm" className="gap-1 text-xs border-teal/30 text-teal hover:bg-teal-dim/20" onClick={() => handleResend(ref)} disabled={resending === ref} data-testid={`resend-full-exp-${ref}`}>
                              {resending === ref ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Resend OB Invite
                            </Button>
                            <Button variant="outline" size="sm" className="gap-1 text-xs border-blue-500/30 text-blue-400 hover:bg-blue-900/20" onClick={() => handleRemind(ref)} disabled={reminding === ref} data-testid={`remind-exp-${ref}`}>
                              {reminding === ref ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Remind Client
                            </Button>
                          </>
                        )}
                        {hasFORF && (
                          <Button size="sm" className="gap-1 text-xs bg-teal hover:bg-teal/90" onClick={() => navigate(`/submission/${rec.draft_forf_id}`)} data-testid={`view-forf-${ref}`}>
                            <ExternalLink className="w-3 h-3" /> View Draft FORF
                          </Button>
                        )}
                        <Button variant="outline" size="sm" className="gap-1 text-xs border-cyan-500/30 text-cyan-400 hover:bg-cyan-900/20" onClick={() => { setShowLinkModal(ref); setLinkMatterRef(""); }} data-testid={`link-ob-${ref}`}>
                          <Link className="w-3 h-3" /> Link OB
                        </Button>
                        {/* Mutex fix (29 Apr 2026): Skip OB is only legal
                            from canonical state ``not_sent``. Once the OB
                            link has gone out the right action is
                            cancel-and-restart, not skip — so the Skip
                            button is hidden in those states to prevent
                            the "OB sent + Skip OB" co-render that Vanessa
                            reported. */}
                        {isDirector && rec.status === "not_sent" && (
                          <Button variant="outline" size="sm" className="gap-1 text-xs border-gray-500/30 text-gray-400 hover:bg-gray-900/20" onClick={() => { setShowSkipModal(ref); setSkipReason(""); setSkipMatterRef(ref); }} data-testid={`skip-ob-${ref}`}>
                            <SkipForward className="w-3 h-3" /> Skip OB
                          </Button>
                        )}
                        {isAdmin && rec.status === "tob_save_failed" && (
                          <Button variant="outline" size="sm" className="gap-1 text-xs border-amber-500/30 text-status-amber hover:bg-status-amber-dim/20" onClick={() => handleRetryFiling(ref)} disabled={retryingFiling === ref} data-testid={`retry-tob-filing-${ref}`} title={rec.tob_save_error ? `Last failure: ${rec.tob_save_error}` : "File the Terms the client already accepted"}>
                            <RefreshCw className="w-3 h-3" /> Retry filing
                          </Button>
                        )}
                        {isAdmin && (rec.status === "sent" || rec.status === "partial" || rec.status === "tob_save_failed") && (
                          <Button variant="outline" size="sm" className="gap-1 text-xs border-amber-500/30 text-status-amber hover:bg-status-amber-dim/20" onClick={() => { setShowCancelModal(ref); setCancelReason(""); }} data-testid={`cancel-ob-${ref}`} title="Cancel the in-flight OB and revert to not-sent">
                            <X className="w-3 h-3" /> Cancel & Restart
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-xs text-txt-muted text-right">{filteredRecords.length} of {records.length} records</p>

      {/* Bulk Delete Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-navy border border-red-500/40 rounded-lg p-5 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()} data-testid="bulk-delete-modal">
            <div className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-status-red" />
              <h3 className="text-lg font-bold text-white">Delete {selectedIds.size} Record{selectedIds.size !== 1 ? "s" : ""}</h3>
            </div>
            <p className="text-sm text-txt-muted">This will permanently delete the selected onboarding records and all linked data (conflict checks, documents, submissions).</p>
            <div className="bg-status-red-dim/20 border border-red-500/20 rounded p-2 max-h-32 overflow-y-auto">
              {[...selectedIds].map(pid => <p key={pid} className="text-xs text-status-red font-mono">{pid}</p>)}
            </div>
            <div>
              <label className="text-[10px] text-status-red/70 uppercase tracking-wider block mb-1">Reason for deletion *</label>
              <Input value={deleteReason} onChange={e => setDeleteReason(e.target.value)}
                placeholder="e.g. Test data cleanup, duplicate entries"
                className="bg-navy-card border-red-700/40 text-white text-xs h-9"
                data-testid="bulk-delete-reason" autoFocus />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => { setShowDeleteConfirm(false); setDeleteReason(""); }} className="text-txt-muted text-xs">Cancel</Button>
              <Button size="sm" onClick={handleBulkDelete} disabled={bulkDeleting || !deleteReason.trim()}
                className="bg-status-red hover:bg-status-red text-white text-xs" data-testid="confirm-bulk-delete-btn">
                {bulkDeleting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                Delete Permanently
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Link OB Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowLinkModal(null)}>
          <div className="bg-navy border border-cyan-500/40 rounded-lg p-5 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()} data-testid="link-ob-modal">
            <div className="flex items-center gap-2">
              <Link className="w-5 h-5 text-cyan-400" />
              <h3 className="text-lg font-bold text-white">Link OB to Matter</h3>
            </div>
            <p className="text-sm text-txt-muted">Link <span className="font-mono text-cyan-300">{showLinkModal}</span> to a different matter reference.</p>
            <div>
              <label className="text-[10px] text-cyan-300/70 uppercase tracking-wider block mb-1">Target Matter Reference *</label>
              <Input value={linkMatterRef} onChange={e => setLinkMatterRef(e.target.value)}
                placeholder="BL.VEC.001.0123" className="bg-navy-card border-cyan-700/40 text-white text-xs h-9"
                data-testid="link-ob-matter-ref" autoFocus />
            </div>
            <div>
              <label className="text-[10px] text-cyan-300/70 uppercase tracking-wider block mb-1">How was OB completed? (optional)</label>
              <textarea value={linkNotes} onChange={e => setLinkNotes(e.target.value)}
                placeholder="e.g. Completed by post on 01/04/2026, paper forms on file"
                className="w-full bg-navy-card border border-cyan-700/40 text-white text-xs rounded-md p-2 h-16 resize-none focus:outline-none focus:ring-1 focus:ring-cyan-500"
                data-testid="link-ob-notes" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowLinkModal(null)} className="text-txt-muted text-xs">Cancel</Button>
              <Button size="sm" onClick={handleLinkOB} disabled={linking || !linkMatterRef.trim()}
                className="bg-cyan-600 hover:bg-cyan-500 text-white text-xs" data-testid="confirm-link-ob-btn">
                {linking ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Link className="w-3 h-3 mr-1" />}
                Link OB
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Skip OB Modal */}
      {showSkipModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowSkipModal(null)}>
          <div className="bg-navy border border-gray-500/40 rounded-lg p-5 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()} data-testid="skip-ob-modal">
            <div className="flex items-center gap-2">
              <SkipForward className="w-5 h-5 text-gray-400" />
              <h3 className="text-lg font-bold text-white">Skip OB</h3>
            </div>
            <p className="text-sm text-txt-muted">Mark <span className="font-mono text-gray-300">{showSkipModal}</span> as skipped? The client will not need to complete onboarding.</p>
            <div>
              <label className="text-[10px] text-gray-300/70 uppercase tracking-wider block mb-1">Matter Reference *</label>
              <Input value={skipMatterRef} onChange={e => setSkipMatterRef(e.target.value)}
                placeholder="BL.VEC.001.0123" className="bg-navy-card border-gray-700/40 text-white text-xs h-9"
                data-testid="skip-ob-matter-ref" />
            </div>
            <div>
              <label className="text-[10px] text-gray-300/70 uppercase tracking-wider block mb-1">Reason *</label>
              <textarea value={skipReason} onChange={e => setSkipReason(e.target.value)}
                placeholder="e.g. Existing company client, admin override"
                className="w-full bg-navy-card border border-gray-700/40 text-white text-xs rounded-md p-2 h-20 resize-none focus:outline-none focus:ring-1 focus:ring-gray-500"
                data-testid="skip-ob-reason" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowSkipModal(null)} className="text-txt-muted text-xs">Cancel</Button>
              <Button size="sm" onClick={handleSkipOB} disabled={skipping || !skipReason.trim() || !skipMatterRef.trim()}
                className="bg-gray-600 hover:bg-gray-500 text-white text-xs" data-testid="confirm-skip-ob-btn">
                {skipping ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <SkipForward className="w-3 h-3 mr-1" />}
                Skip OB
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Ambiguous OB resend / remind modal. Surfaced when the backend returns
          HTTP 409 because the supplied ref matches more than one onboarding
          record. The operator MUST pick a specific candidate before the
          tokenised link is sent — silent first-pick is forbidden. */}
      {ambiguousResend && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
             onClick={() => !ambiguousRetryBusy && setAmbiguousResend(null)}>
          <div className="bg-navy border border-status-red/50 rounded-lg p-5 max-w-2xl w-full space-y-4"
               onClick={e => e.stopPropagation()} data-testid="ambiguous-resend-modal">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-status-red" />
              <h3 className="text-lg font-bold text-white">
                Ambiguous {ambiguousResend.action === "remind" ? "Reminder" : "Resend"} — Pick a Client
              </h3>
            </div>
            <p className="text-sm text-txt-muted">
              The reference <span className="font-mono text-status-red">{ambiguousResend.ref}</span>{" "}
              matches <span className="text-white">{ambiguousResend.candidates.length}</span> onboarding records.
              The {ambiguousResend.action === "remind" ? "reminder" : "resend"} was{" "}
              <span className="text-status-red">not sent</span> — pick the correct client below to retry,
              or cancel.
            </p>
            <div className="border border-navy-border rounded-md divide-y divide-navy-border max-h-96 overflow-y-auto"
                 data-testid="ambiguous-candidate-list">
              {ambiguousResend.candidates.map((cand, idx) => {
                const targetRef = cand.matter_reference || cand.provisional_id;
                const last = cand.last_activity
                  ? (() => { try { return fmtUKShort(cand.last_activity); } catch { return cand.last_activity; } })()
                  : "—";
                return (
                  <div key={`${targetRef}-${idx}`}
                       className="p-3 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-navy-hover"
                       data-testid={`ambiguous-candidate-${targetRef}`}>
                    <div className="flex-1 min-w-0 space-y-0.5">
                      <p className="text-sm font-mono text-cyan-300 truncate">{targetRef}</p>
                      <p className="text-sm text-white truncate">{cand.client_name || "(no name)"}</p>
                      <p className="text-xs text-txt-muted truncate">{cand.client_email || "(no email)"}</p>
                      <p className="text-[11px] text-txt-muted">
                        Status: <span className="text-slate-300">{cand.status || "unknown"}</span>{" · "}
                        Last activity: <span className="text-slate-300">{last}</span>
                      </p>
                    </div>
                    <Button size="sm"
                      onClick={() => retryAmbiguous(targetRef)}
                      disabled={ambiguousRetryBusy || !targetRef}
                      className="bg-teal hover:bg-teal text-white text-xs whitespace-nowrap"
                      data-testid={`ambiguous-pick-${targetRef}`}>
                      {ambiguousRetryBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Mail className="w-3 h-3 mr-1" />}
                      {ambiguousResend.action === "remind" ? "Send Reminder" : "Resend OB"}
                    </Button>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm"
                onClick={() => setAmbiguousResend(null)}
                disabled={ambiguousRetryBusy}
                className="text-txt-muted text-xs"
                data-testid="ambiguous-cancel-btn">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel & Restart OB Modal — replaces "Skip OB" while link is in flight. */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowCancelModal(null)}>
          <div className="bg-navy border border-amber-500/40 rounded-lg p-5 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()} data-testid="cancel-ob-modal">
            <div className="flex items-center gap-2">
              <X className="w-5 h-5 text-status-amber" />
              <h3 className="text-lg font-bold text-white">Cancel In-Flight OB</h3>
            </div>
            <p className="text-sm text-txt-muted">
              Cancel the OB for <span className="font-mono text-status-amber">{showCancelModal}</span> and revert it to <span className="text-white">Not Sent</span>?
              The audit trail records the previous status and your reason. From Not Sent you can re-send with corrected details, or skip with a reason.
            </p>
            <div>
              <label className="text-[10px] text-status-amber/70 uppercase tracking-wider block mb-1">Reason *</label>
              <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                placeholder="e.g. Wrong client email — restart with corrected address"
                className="w-full bg-navy-card border border-amber-700/40 text-white text-xs rounded-md p-2 h-20 resize-none focus:outline-none focus:ring-1 focus:ring-amber-500"
                data-testid="cancel-ob-reason" autoFocus />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowCancelModal(null)} className="text-txt-muted text-xs">Keep Sent</Button>
              <Button size="sm" onClick={handleCancelAndRestart} disabled={cancelling || !cancelReason.trim()}
                className="bg-status-amber hover:bg-status-amber text-white text-xs" data-testid="confirm-cancel-ob-btn">
                {cancelling ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <X className="w-3 h-3 mr-1" />}
                Cancel & Restart
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value, capitalize, color }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-txt-muted mb-0.5">{label}</p>
      <p className={`text-sm ${color || "text-txt-muted"} ${capitalize ? "capitalize" : ""}`}>{value || "-"}</p>
    </div>
  );
}
